import { Button, Divider, Stack } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { Controller, FieldPath } from 'react-hook-form';
import { CampaignFields, CampaignFormInputs } from 'types';
import { validateUrl } from 'utils';
import { PrefixTextField } from 'components/PrefixTextFieldProps';
import { useCampaignForm, useSlidingDelay } from 'hooks';
import { websiteUrlIndexAttrName } from 'shared';

export type WebsiteEditorProps = {
  index: number;
  hasWebsitesInitially: boolean;
};

const urlInputPrefix = 'https://';

export const WebsiteEditor: FC<WebsiteEditorProps> = ({ index, hasWebsitesInitially }) => {
  const {
    control,
    setValue,
    resetField,
    formState: { errors },
    websites,
    updateWebsite,
    removeWebsite,
    addNewWebsite,
    validateWebsite,
    setWebsiteEditing,
  } = useCampaignForm();
  const website = websites[index];

  const onSubmit = async () => {
    // trigger field validation before save. if invalid - error is settled in the trigger inside validateUrl
    const isValidUrl = await validateWebsite(index);
    if (isValidUrl && website?.url !== '') {
      updateWebsite(index, {});
      setWebsiteEditing(index, false);
    }
  };

  const onCancel = async () => {
    if (website?.url !== '') {
      const fieldName = `${CampaignFields.websites}.${index}.url` as FieldPath<CampaignFormInputs>;
      resetField(fieldName);
      if (await validateWebsite(index)) setWebsiteEditing(index, false);
    } else if (websites.length > 1) {
      removeWebsite(index);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    switch (e.key) {
      case 'Enter':
        if (!(e.target as HTMLElement).matches?.('button')) onSubmit();
        break;
      case 'Escape':
        onCancel();
        break;
      case 'Tab':
        if (!hasWebsitesInitially && website?.url !== '' && (await validateWebsite(index))) {
          e.preventDefault();
          addNewWebsite();
        }
        break;
    }
  };

  const otherUrls = useMemo(
    () => new Set(websites.map((e, i) => (index === i ? undefined : e.url)).filter(Boolean)),
    [index, websites]
  );

  const validate = useSlidingDelay((val: string) => validateUrl(val, otherUrls), 100);

  return (
    <Stack gap={4} mb={6} width={{ xs: 1, lg: 2 / 3 }} onKeyDown={handleKeyDown}>
      <Controller<CampaignFormInputs>
        control={control}
        name={`${CampaignFields.websites}.${index}.url`}
        rules={{ validate }}
        render={({ field: { value } }) => {
          return (
            <PrefixTextField
              label="Gib eine Webseite ein"
              value={value}
              prefix={urlInputPrefix}
              prefixMatch={/^https?:\/\//}
              helperText={errors.websites?.[index]?.url?.message}
              error={!!errors.websites?.[index]?.url?.message}
              inputAttributes={{
                [websiteUrlIndexAttrName]: index.toFixed(0),
                'data-testid': `website:${index}:url`,
              }}
              onChange={(url) => {
                url = url === urlInputPrefix ? '' : url;
                // TODO: remove type assertion once react-hook-form fixed FieldPathValue for dynamic fields
                setValue(`${CampaignFields.websites}.${index}.url`, url as never, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
          );
        }}
      />
      {hasWebsitesInitially && (
        <Stack flexDirection="row">
          <Button size="small" variant="outlined" sx={{ mr: 4 }} onClick={onCancel}>
            Abbrechen
          </Button>
          <Button size="small" variant="contained" onClick={onSubmit}>
            änderung speichern
          </Button>
          <Divider sx={{ mb: 4 }} />
        </Stack>
      )}
    </Stack>
  );
};
